import * as Sentry from '@sentry/browser';
import {
    type Asset,
    AudioAsset,
    AudioObject,
    Dimension,
    DynamicEventEmitter,
    equals,
    FontAsset,
    type FontAssetParams,
    type IAssetsLoader,
    ImageAsset,
    loadFont,
    sleep,
    VideoAsset,
    VideoObject,
} from '@bynder-studio/render-core';
import { cleanS3Url } from '@bynder-studio/misc';
import createBrowserMediaController, { connectMediaNodes, updateMediaController } from './createBrowserMediaController';
import getImageFrame from './getImageFrame';

const BASE_RETRY_TIMEOUT = 3_000;
// Native media elements (<img>/<video>/<audio>) have no load timeout and won't
// reject on a request that stalls without erroring. These caps abort a stalled
// load so the retry logic can advance (previously the service worker did this).
const IMAGE_LOAD_TIMEOUT = 15_000;
const MEDIA_LOAD_TIMEOUT = 30_000;

const getErrorText = (type: string) => `Failed to load asset (${type})`;

export class BrowserAssetsLoader implements IAssetsLoader {
    eventEmitter: DynamicEventEmitter = new DynamicEventEmitter();

    private assets: Map<string, Asset>;

    private waiting: {
        [key: string]: Function[];
    };

    private glyphPath: {
        [fontId: number | string]: {
            [glyphId: number]: Path2D;
        };
    } = {};

    constructor() {
        this.assets = new Map();
        this.waiting = {};
    }

    setAsset(asset: Asset) {
        const oldAsset = this.assets.get(asset.id);

        if (oldAsset && cleanS3Url(oldAsset.src) === cleanS3Url(asset.src)) {
            const v1 = oldAsset as VideoAsset;
            const v2 = asset as VideoAsset;

            if ((oldAsset instanceof VideoAsset || oldAsset instanceof AudioAsset) && oldAsset.object) {
                if (
                    v1.startFrame !== v2.startFrame ||
                    v1.offsetTime !== v2.offsetTime ||
                    v1.frameRate !== v2.frameRate ||
                    v1.duration !== v2.duration
                ) {
                    if (oldAsset.object.mediaController) {
                        oldAsset.object.mediaController.destroy();
                    }

                    oldAsset.object.mediaController = createBrowserMediaController.call(oldAsset.object, asset);
                    connectMediaNodes(oldAsset.object);
                }
            }

            Object.keys(asset).forEach((key) => {
                if (key === 'frameBuffer') {
                    if (
                        v1.startFrame === v2.startFrame &&
                        v1.offsetTime === v2.offsetTime &&
                        v1.frameRate === v2.frameRate &&
                        v1.duration === v2.duration &&
                        v1.isAlpha === v2.isAlpha &&
                        v1.naturalDimension.equals(v2.naturalDimension)
                    ) {
                        oldAsset[key] = asset[key];
                    }

                    return;
                }

                if (['object', 'objectClone', 'accessible', 'loading', 'width', 'height', 'buffering'].includes(key)) {
                    return;
                }

                if (asset[key] !== undefined) {
                    oldAsset[key] = asset[key];
                }
            });

            return;
        }

        this.destroyAsset(asset.id);
        this.assets.set(asset.id, asset);
    }

    clearAssets() {
        this.assets = new Map();
    }

    getFonts() {
        const fonts: (FontAssetParams & { object: ArrayBuffer })[] = [];
        this.assets.forEach((asset) => {
            if (asset instanceof FontAsset && asset.object) {
                fonts.push({
                    id: asset.id,
                    src: asset.src,
                    fontId: asset.fontId,
                    object: asset.object,
                });
            }
        });

        return fonts;
    }

    setFonts(fonts: (FontAssetParams & { object: ArrayBuffer })[]) {
        fonts.forEach((font) => {
            const fontAsset = new FontAsset({
                id: font.id,
                src: font.src,
                fontId: font.fontId,
            });
            fontAsset.object = font.object;
            this.setAsset(fontAsset);
        });
    }

    getAsset<T extends Asset>(assetId: string): T {
        return this.assets.get(assetId) as T;
    }

    destroyAsset(assetId: string): void {
        if (!this.assets.has(assetId)) {
            return;
        }

        const asset = this.assets.get(assetId);
        this.assets.delete(assetId);

        if (asset instanceof VideoAsset || asset instanceof AudioAsset) {
            asset.destroy();
        }
    }

    getAllAssets(): Map<string, Asset> {
        return this.assets;
    }

    updateAsset(id: string, data: any): void {
        const isAssetChanged = (asset) =>
            Object.entries(data).some(
                ([key, value]) =>
                    !equals(
                        value,
                        typeof asset[key] === 'object' && 'toObject' in asset[key] ? asset[key].toObject() : asset[key],
                    ),
            );

        this.assets.forEach((asset) => {
            if (asset.id === id && isAssetChanged(asset)) {
                const dataToUpdate = { ...data };

                if (data.naturalDimension) {
                    dataToUpdate.naturalDimension = new Dimension(
                        data.naturalDimension.width,
                        data.naturalDimension.height,
                    );
                }

                if (asset instanceof VideoAsset || asset instanceof AudioAsset) {
                    asset.destroy();
                }

                asset.update(dataToUpdate);
                this.loadAsset(asset);
            }
        });
    }

    private loadAsset(asset: Asset) {
        // todo: add type property to asset and use switch
        if (asset instanceof ImageAsset) {
            return this.loadImage(asset);
        }

        if (asset instanceof VideoAsset) {
            return this.loadVideo(asset);
        }

        if (asset instanceof AudioAsset) {
            return this.loadAudio(asset);
        }

        if (asset instanceof FontAsset) {
            return this.loadFont(asset);
        }
    }

    private dispatchLoadEvent(asset: Asset, type: 'image' | 'video' | 'audio' | 'font') {
        queueMicrotask(() =>
            this.eventEmitter.emit('asset.load', {
                asset,
                type,
            }),
        );
    }

    loadImage(asset: ImageAsset): Promise<ImageAsset> {
        const imageAsset = this.getAsset(asset.id) as ImageAsset;

        if (imageAsset.isAccessible()) {
            return Promise.resolve(imageAsset);
        }

        if (this.waiting[imageAsset.id]) {
            return new Promise((resolve) => {
                this.waiting[imageAsset.id].push(resolve);
            });
        }

        this.waiting[imageAsset.id] = [];
        imageAsset.setLoading(true);

        const loadedAsset = loadImageWithRetry(imageAsset);

        loadedAsset.then(() => {
            this.waiting[imageAsset.id].forEach((cb) => cb(imageAsset));
            delete this.waiting[imageAsset.id];

            this.dispatchLoadEvent(imageAsset, 'image');
        });

        return loadedAsset;
    }

    loadVideo(asset: VideoAsset): Promise<VideoAsset> {
        const videoAsset = this.getAsset(asset.id) as VideoAsset;

        if (videoAsset.isAccessible()) {
            return Promise.resolve(videoAsset);
        }

        if (this.waiting[videoAsset.id]) {
            return new Promise((resolve) => {
                this.waiting[videoAsset.id].push(resolve);
            });
        }

        this.waiting[videoAsset.id] = [];

        videoAsset.setLoading(true);
        videoAsset.objectClone = null;

        const loadingAsset = loadMediaElementWithRetry(videoAsset, 'video');

        loadingAsset.then(() => {
            this.waiting[videoAsset.id].forEach((cb) => cb(videoAsset));
            delete this.waiting[videoAsset.id];

            this.dispatchLoadEvent(videoAsset, 'video');
        });

        return loadingAsset;
    }

    loadAudio(asset: AudioAsset): Promise<AudioAsset> {
        const audioAsset = this.getAsset(asset.id) as AudioAsset;

        if (audioAsset.isAccessible()) {
            updateMediaController(audioAsset);

            return Promise.resolve(audioAsset);
        }

        if (this.waiting[audioAsset.id]) {
            return new Promise((resolve) => {
                this.waiting[audioAsset.id].push(resolve);
            });
        }

        this.waiting[audioAsset.id] = [];
        audioAsset.setLoading(true);

        const loadingAsset = loadMediaElementWithRetry(audioAsset, 'audio');

        loadingAsset.then(() => {
            this.waiting[audioAsset.id]?.forEach((cb) => cb(audioAsset));
            delete this.waiting[audioAsset.id];

            this.dispatchLoadEvent(audioAsset, 'audio');
        });

        return loadingAsset;
    }

    loadFont(fontAsset: FontAsset): Promise<FontAsset> {
        if (this.assets.has(fontAsset.id)) {
            const asset = this.assets.get(fontAsset.id);

            if (asset.object) {
                return new Promise((resolve) => {
                    resolve(this.assets.get(asset.id) as FontAsset);
                });
            }
        }

        if (this.waiting[fontAsset.id]) {
            return new Promise((resolve) => {
                this.waiting[fontAsset.id].push(resolve);
            });
        }

        this.waiting[fontAsset.id] = [];
        fontAsset.setLoading(true);

        const loadedAsset = loadFontWithRetry(fontAsset, this.eventEmitter, 1)
            .then((arrayBuffer) => {
                fontAsset.object = arrayBuffer;
                fontAsset.setAccessible(true);
                fontAsset.setLoading(false);
                this.assets.set(fontAsset.id, fontAsset);

                return fontAsset;
            })
            .catch((err) => {
                fontAsset.setAccessible(false);
                fontAsset.setLoading(false);

                return err;
            });

        loadedAsset.then(() => {
            this.waiting[fontAsset.id].forEach((cb) => cb(fontAsset));
            delete this.waiting[fontAsset.id];

            this.dispatchLoadEvent(fontAsset, 'font');
        });

        return loadedAsset;
    }

    async loadAll() {
        const promises = [];
        this.assets.forEach((asset) => {
            if (asset instanceof FontAsset) {
                promises.push(this.loadFont(asset));
            } else if (asset instanceof ImageAsset) {
                promises.push(this.loadImage(asset));
            } else if (asset instanceof VideoAsset) {
                promises.push(this.loadVideo(asset));
            } else if (asset instanceof AudioAsset) {
                promises.push(this.loadAudio(asset));
            }
        });
        const loadedAssets = await Promise.all(promises.filter((p) => p));
        loadedAssets.forEach((loadedAsset) => {
            this.assets.set(loadedAsset.id, loadedAsset);
        });
    }

    makePath(glyph): Path2D {
        const { fontId, glyphId, path } = glyph;

        if (!this.glyphPath[fontId]) {
            this.glyphPath[fontId] = {};
        }

        if (!this.glyphPath[fontId][glyphId]) {
            this.glyphPath[fontId][glyphId] = new Path2D(path);
        }

        return this.glyphPath[fontId][glyphId];
    }
}

function sendErrorToSentry(error: string, asset: Asset, attempts: number) {
    Sentry.captureMessage(error, {
        contexts: {
            'asset.data': {
                src: asset.src,
                attempts,
            },
        },
    });
}

async function loadFontWithRetry(asset: FontAsset, eventEmitter, attempts = 1) {
    let arrayBuffer: ArrayBuffer;

    try {
        arrayBuffer = await loadFont(asset.src);
    } catch (err) {
        console.warn(getErrorText('font'), asset.src);

        // emit error event only on first attempt or second attempt
        // first one can be missed because of the async nature of the function
        if (attempts === 1 || attempts === 2) {
            eventEmitter.emit('font.load.error');
        }
    }

    if (arrayBuffer) {
        return arrayBuffer;
    }

    if (attempts > 5) {
        sendErrorToSentry(getErrorText('font'), asset, attempts);
        throw new Error(getErrorText('font'));
    }

    await sleep(getTimeoutFromAttempt(attempts));

    return loadFontWithRetry(asset, eventEmitter, attempts + 1);
}

async function fetchImageObjectUrl(src: string, signal: AbortSignal): Promise<string> {
    // `cache: 'no-store'` is the crux of the fix: it bypasses the browser's shared
    // HTTP cache entirely. S3 only emits `Vary: Origin` / `Access-Control-Allow-Origin`
    // on requests that carry an `Origin` header, so a cached response can lack those
    // headers and poison subsequent crossOrigin reads (leaving the canvas blank).
    // Going through the cache neither reads nor writes that poisoned entry.
    const response = await fetch(src, { mode: 'cors', cache: 'no-store', signal });

    if (!response.ok) {
        throw new Error(`Failed to fetch image asset (${response.status})`);
    }

    const blob = await response.blob();

    return URL.createObjectURL(await normalizeSvgBlob(blob));
}

// A viewBox-only SVG (no width/height attributes) decodes to Chrome's default
// 300x150 fit (e.g. 117x150), so it renders as a tiny image blown up to fill the
// element box. Inject width/height from the viewBox so it decodes at full size.
async function normalizeSvgBlob(blob: Blob): Promise<Blob> {
    if (blob.type !== 'image/svg+xml') {
        return blob;
    }

    const text = await blob.text();
    const svgMatch = text.match(/<svg\b[^>]*>/i);

    if (!svgMatch) {
        return blob;
    }

    const openTag = svgMatch[0];

    if (/\bwidth\s*=/.test(openTag) || /\bheight\s*=/.test(openTag)) {
        return blob;
    }

    // viewBox = "min-x min-y width height"; min-x/min-y can be negative, and the
    // four values may be separated by whitespace and/or a comma. Only width/height
    // (the last two) are relevant and are always non-negative.
    const num = '-?[\\d.]+';
    const sep = '[\\s,]+';
    const viewBox = openTag.match(
        new RegExp(`viewBox\\s*=\\s*["']\\s*${num}${sep}${num}${sep}([\\d.]+)${sep}([\\d.]+)\\s*["']`, 'i'),
    );

    if (!viewBox) {
        return blob;
    }

    const patched = openTag.replace(/<svg\b/i, `<svg width="${viewBox[1]}" height="${viewBox[2]}"`);

    return new Blob([text.replace(openTag, patched)], { type: 'image/svg+xml' });
}

async function loadImageWithRetry(asset: ImageAsset, attempt = 1): Promise<ImageAsset> {
    let objectUrl: string | undefined;

    try {
        // Load the bytes via a CORS fetch instead of a crossOrigin <img>, then render
        // from the resulting same-origin blob: URL. The fetch always carries an Origin
        // (so S3 returns the CORS headers) and `no-store` avoids the poisoned cache,
        // while the blob: URL keeps the canvas untainted so export still works.
        const controller = new AbortController();

        objectUrl = await withTimeout(fetchImageObjectUrl(asset.src, controller.signal), IMAGE_LOAD_TIMEOUT, () => {
            // Abort a stalled request so the load fails fast and is retried.
            controller.abort();
        });

        const image = createImageElement(objectUrl);
        await image.decode();

        asset.setMediaData(image);
        asset.setAccessible(true);
        asset.setLoading(false);

        return asset;
    } catch (err) {
        console.warn(getErrorText('image'), asset.src, `attempt=${attempt}`, err);

        if (attempt >= 5) {
            asset.setAccessible(false);
            asset.setLoading(false);

            sendErrorToSentry(getErrorText('image'), asset, attempt);
            throw new Error('Failed to load image asset', { cause: err });
        }

        await sleep(getTimeoutFromAttempt(attempt));

        return loadImageWithRetry(asset, attempt + 1);
    } finally {
        // Once decoding has settled the element retains the decoded image, so the
        // blob URL can be released (each attempt releases its own).
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
    }
}

function loadMediaElementWithRetry<T extends VideoAsset | AudioAsset>(asset: T, type: 'video' | 'audio'): Promise<T> {
    return new Promise((res, rej) => {
        let element: AudioObject | VideoObject;

        if (type === 'video') {
            element = createVideoElement(asset as VideoAsset);
        } else if (type === 'audio') {
            element = createAudioElement(asset as AudioAsset);
        } else {
            rej('unknown asset type');
        }

        let attempt = 1;

        // Guard against a load that stalls without ever firing 'error' (the
        // 'error' event drives the retry loop, but a hung request never emits it).
        const loadTimeoutId = setTimeout(() => {
            failWithError();
        }, MEDIA_LOAD_TIMEOUT);

        const retryWithTimeout = () => {
            setTimeout(() => {
                element.load();
            }, getTimeoutFromAttempt(attempt));
        };

        const failWithError = () => {
            clearTimeout(loadTimeoutId);
            console.warn(getErrorText(type), asset.src);
            sendErrorToSentry(getErrorText(type), asset, attempt);

            asset.setAccessible(false);
            rej(getErrorText(type));
        };

        const onError = () => {
            if (++attempt > 5) {
                failWithError();
            } else {
                retryWithTimeout();
            }
        };

        const onLoadedmetadata = () => {
            clearTimeout(loadTimeoutId);
            element.removeEventListener('error', onError);

            if (type === 'video') {
                const elementClone = element.cloneNode(true) as VideoObject;
                element.mediaController = createBrowserMediaController.call(element, asset);
                connectMediaNodes(element);
                (asset as VideoAsset).setMediaData(element as VideoObject, elementClone, getImageFrame);
            } else if (type === 'audio') {
                element.mediaController = createBrowserMediaController.call(element, asset);
                connectMediaNodes(element);
                (asset as AudioAsset).setMediaData(element as AudioObject);
            }

            asset.setAccessible(true);
            asset.setLoading(false);

            res(asset);
        };

        element.addEventListener('loadedmetadata', onLoadedmetadata, {
            once: true,
        });

        element.addEventListener('error', onError);
    });
}

function createImageElement(src: string) {
    const image = new Image();
    // `src` is a same-origin blob: URL, so no crossOrigin handshake is needed and
    // the canvas stays untainted.
    image.src = src;

    return image;
}

function isSafari() {
    const userAgent = navigator.userAgent;

    // Check if the browser is Safari and not Chrome, since Chrome includes "Safari" in its userAgent
    return /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
}

function createVideoElement(asset: VideoAsset) {
    const video = document.createElement('video');
    video.id = asset.id;
    video.muted = !asset.useAudio;
    video.autoplay = false;
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.volume = 0.2;

    if (isSafari()) {
        // #t=0.001 is a workaround for a bug in Safari where the video first frame is not displayed
        // due to preload parameter set to metadata only. Related link:
        // https://stackoverflow.com/questions/41255841/how-to-get-html5-video-thumbnail-without-using-poster-on-safari-or-ios
        video.src = asset.src + '#t=0.001';
    } else {
        video.src = asset.src;
    }

    return video as VideoObject;
}

function createAudioElement(asset: AudioAsset) {
    const audio = document.createElement('audio');
    audio.id = asset.id;
    audio.muted = false;
    audio.autoplay = false;
    audio.crossOrigin = 'anonymous';
    audio.preload = 'metadata';
    audio.volume = 0.2;
    audio.src = asset.src;

    return audio as AudioObject;
}

function getTimeoutFromAttempt(attempt: number) {
    return BASE_RETRY_TIMEOUT * attempt;
}

export function withTimeout<T>(promise: Promise<T>, ms: number, onTimeout?: () => void): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            onTimeout?.();
            reject(new Error('Asset load timed out'));
        }, ms);

        promise.then(
            (value) => {
                clearTimeout(timeoutId);
                resolve(value);
            },
            (error: unknown) => {
                clearTimeout(timeoutId);
                reject(error instanceof Error ? error : new Error('Asset load failed', { cause: error }));
            },
        );
    });
}
